import React, { useState, useEffect } from 'react';
import { useAppStore } from '../../store';
import { getFeedbackTopicDetail, createFeedbackReply, updateFeedbackTopic, deleteFeedbackTopic, deleteFeedbackReply } from '../../services/feedbackApi';
import type { FeedbackTopic, FeedbackReply } from '../../types';

interface FeedbackDetailModalProps {
  show: boolean;
  topic: FeedbackTopic | null;
  onHide: () => void;
  onTopicUpdate?: () => void;
}

const FeedbackDetailModal: React.FC<FeedbackDetailModalProps> = ({
  show,
  topic,
  onHide,
  onTopicUpdate
}) => {
  const { user, isAuthenticated } = useAppStore();
  
  const [topicDetail, setTopicDetail] = useState<FeedbackTopic | null>(null);
  const [replies, setReplies] = useState<FeedbackReply[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [adminPanelExpanded, setAdminPanelExpanded] = useState(false);

  // 加载主题详情
  const loadTopicDetail = async () => {
    if (!topic) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getFeedbackTopicDetail(topic.id);
      if (response.success) {
        setTopicDetail(response.data.topic);
        setReplies(response.data.replies || []);
      } else {
        setError(response.message || '加载失败');
      }
    } catch (err: any) {
      console.error('加载反馈详情失败:', err);
      setError('加载反馈详情失败');
    } finally {
      setLoading(false);
    }
  };

  // 提交回复
  const handleSubmitReply = async () => {
    if (!topic || !replyContent.trim()) return;
    
    setSubmittingReply(true);
    setError(null);
    
    try {
      const response = await createFeedbackReply(topic.id, {
        content: replyContent.trim()
      });
      
      if (response.success) {
        setReplyContent('');
        await loadTopicDetail(); // 重新加载以显示新回复
        if (onTopicUpdate) {
          onTopicUpdate();
        }
      } else {
        setError(response.message || '回复失败');
      }
    } catch (err: any) {
      console.error('提交回复失败:', err);
      setError('提交回复失败');
    } finally {
      setSubmittingReply(false);
    }
  };

  // 处理状态变更
  const handleStatusChange = async (newStatus: string) => {
    if (!topic || !topicDetail) return;

    setSubmittingReply(true);
    setError(null);

    try {
      await updateFeedbackTopic(topic.id, { status: newStatus });
      await loadTopicDetail(); // 重新加载以显示更新
      if (onTopicUpdate) {
        onTopicUpdate();
      }
    } catch (err: any) {
      console.error('更新状态失败:', err);
      setError('更新状态失败');
    } finally {
      setSubmittingReply(false);
    }
  };

  // 处理优先级变更
  const handlePriorityChange = async (newPriority: FeedbackTopic['priority']) => {
    if (!topic || !topicDetail) return;

    setSubmittingReply(true);
    setError(null);

    try {
      await updateFeedbackTopic(topic.id, { priority: newPriority });
      await loadTopicDetail(); // 重新加载以显示更新
      if (onTopicUpdate) {
        onTopicUpdate();
      }
    } catch (err: any) {
      console.error('更新优先级失败:', err);
      setError('更新优先级失败');
    } finally {
      setSubmittingReply(false);
    }
  };

  // 处理置顶切换
  const handleTogglePin = async () => {
    if (!topic || !topicDetail) return;

    setSubmittingReply(true);
    setError(null);

    try {
      await updateFeedbackTopic(topic.id, { is_pinned: !topicDetail.is_pinned });
      await loadTopicDetail(); // 重新加载以显示更新
      if (onTopicUpdate) {
        onTopicUpdate();
      }
    } catch (err: any) {
      console.error('更新置顶状态失败:', err);
      setError('更新置顶状态失败');
    } finally {
      setSubmittingReply(false);
    }
  };

  // 处理删除主题
  const handleDeleteTopic = async () => {
    if (!topic) return;

    const confirmed = window.confirm('确定要删除这个反馈主题吗？此操作不可撤销！');
    if (!confirmed) return;

    setSubmittingReply(true);
    setError(null);

    try {
      await deleteFeedbackTopic(topic.id);
      if (onTopicUpdate) {
        onTopicUpdate();
      }
      onHide(); // 关闭模态框
    } catch (err: any) {
      console.error('删除主题失败:', err);
      setError(err.message || '删除主题失败');
    } finally {
      setSubmittingReply(false);
    }
  };

  // 删除回复
  const handleDeleteReply = async (replyId: number) => {
    if (!window.confirm('确定要删除这条回复吗？此操作不可恢复。')) {
      return;
    }

    setSubmittingReply(true);
    setError(null);

    try {
      await deleteFeedbackReply(replyId);
      // 重新加载主题详情以更新回复列表
      await loadTopicDetail();
      if (onTopicUpdate) {
        onTopicUpdate();
      }
    } catch (err: any) {
      console.error('删除回复失败:', err);
      setError(err.message || '删除回复失败');
    } finally {
      setSubmittingReply(false);
    }
  };

  // 获取状态徽章样式
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'open': return 'bg-primary';
      case 'in_progress': return 'bg-warning';
      case 'resolved': return 'bg-success';
      case 'closed': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  };

  // 获取状态文本
  const getStatusText = (status: string) => {
    switch (status) {
      case 'open': return '待处理';
      case 'in_progress': return '处理中';
      case 'resolved': return '已解决';
      case 'closed': return '已关闭';
      default: return '未知';
    }
  };

  // 格式化时间
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN');
  };

  // 当topic变化时重新加载详情
  useEffect(() => {
    if (show && topic) {
      loadTopicDetail();
    }
  }, [show, topic]);

  // 重置状态当模态框关闭时
  useEffect(() => {
    if (!show) {
      setTopicDetail(null);
      setReplies([]);
      setReplyContent('');
      setError(null);
      setAdminPanelExpanded(false); // 重置管理员面板折叠状态
    }
  }, [show]);

  // 处理点击遮罩层关闭模态框
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onHide();
    }
  };

  if (!show || !topic) return null;

  return (
    <div className={`feedback-detail-overlay ${show ? 'show' : ''}`} onClick={handleOverlayClick}>
      <div className="feedback-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="feedback-detail-header">
          <div className="feedback-detail-title">
            <h3>
              <i className="fas fa-comment-dots me-2"></i>
              {topicDetail?.title || topic.title}
            </h3>
            <span className={`badge ${getStatusBadgeClass(topicDetail?.status || topic.status)}`}>
              {getStatusText(topicDetail?.status || topic.status)}
            </span>
          </div>
          <button className="feedback-detail-close" onClick={onHide} title="关闭">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="feedback-detail-content">
          {loading && (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">加载中...</span>
              </div>
            </div>
          )}

          {error && (
            <div className="alert alert-danger mb-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          {topicDetail && (
            <>
              {/* 主题信息 */}
              <div className="feedback-topic-info">
                <div className="topic-meta-row">
                  <div className="topic-meta">
                    <span className="meta-item">
                      <i className="fas fa-user"></i>
                      {topicDetail.author_name}
                    </span>
                    <span className="meta-item">
                      <i className="fas fa-clock"></i>
                      {formatDate(topicDetail.created_at)}
                    </span>
                    <span className="meta-item">
                      <i className="fas fa-eye"></i>
                      {topicDetail.view_count} 次浏览
                    </span>
                  </div>
                  <span className={`category-badge ${topicDetail.category_name === '功能建议' ? 'feature' : 'bug'}`}>
                    {topicDetail.category_name}
                  </span>
                </div>

                <div className="topic-content">
                  <div
                    className="content-text"
                    dangerouslySetInnerHTML={{
                      __html: topicDetail.content?.replace(/\n/g, '<br>') || ''
                    }}
                  />
                </div>
              </div>

              {/* 回复列表 */}
              <div className="replies-section">
                <h6 className="replies-title">
                  <i className="fas fa-comments"></i>
                  回复 ({replies.length})
                </h6>

                {replies.length === 0 ? (
                  <div className="no-replies">
                    <i className="fas fa-comment-slash"></i>
                    <p>暂无回复</p>
                  </div>
                ) : (
                  <div className="replies-list">
                    {replies.map((reply) => (
                      <div key={reply.id} className="reply-item">
                        <div className="reply-header">
                          <div className="reply-meta">
                            <strong className="reply-author">
                              {reply.author_name}
                              {reply.user?.is_admin && (
                                <span className="badge bg-danger ms-1">管理员</span>
                              )}
                            </strong>
                            <span className="reply-time">{formatDate(reply.created_at)}</span>
                          </div>
                          {/* 管理员删除按钮 */}
                          {user?.is_admin && (
                            <button
                              className="btn btn-sm btn-outline-danger reply-delete-btn"
                              onClick={() => handleDeleteReply(reply.id)}
                              disabled={submittingReply}
                              title="删除回复"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </div>
                        <div
                          className="reply-content"
                          dangerouslySetInnerHTML={{
                            __html: reply.content?.replace(/\n/g, '<br>') || ''
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 回复表单 */}
              {isAuthenticated && (
                <div className="reply-form">
                  <h6 className="reply-form-title">添加回复</h6>
                  <div className="reply-form-content">
                    <textarea
                      className="form-control reply-textarea"
                      rows={4}
                      placeholder="请输入您的回复..."
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      disabled={submittingReply}
                    />
                  </div>
                  <div className="reply-form-actions">
                    <button
                      className="btn btn-primary reply-submit-btn"
                      onClick={handleSubmitReply}
                      disabled={!replyContent.trim() || submittingReply}
                    >
                      {submittingReply ? (
                        <>
                          <div className="spinner-border spinner-border-sm me-2"></div>
                          提交中...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane me-2"></i>
                          提交回复
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {!isAuthenticated && (
                <div className="login-prompt">
                  <p>
                    <i className="fas fa-info-circle"></i>
                    请先登录后再参与讨论
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="feedback-detail-footer">
          {/* 管理员操作区域 */}
          {user?.is_admin && topicDetail && (
            <div className="admin-actions">
              <div className="admin-section">
                <div
                  className="admin-section-header"
                  onClick={() => setAdminPanelExpanded(!adminPanelExpanded)}
                >
                  <h5 className="admin-section-title">
                    <i className="fas fa-cog me-2"></i>
                    管理员操作
                  </h5>
                  <i className={`fas fa-chevron-${adminPanelExpanded ? 'up' : 'down'} admin-toggle-icon`}></i>
                </div>

                {adminPanelExpanded && (
                  <div className="admin-section-content">
                    <div className="admin-controls">
                      <div className="admin-control-group">
                        <label className="admin-label">状态:</label>
                        <select
                          className="form-select admin-select"
                          value={topicDetail.status}
                          onChange={(e) => handleStatusChange(e.target.value)}
                          disabled={submittingReply}
                        >
                          <option value="open">待处理</option>
                          <option value="in_progress">处理中</option>
                          <option value="resolved">已解决</option>
                          <option value="closed">已关闭</option>
                        </select>
                      </div>

                      <div className="admin-control-group">
                        <label className="admin-label">优先级:</label>
                        <select
                          className="form-select admin-select"
                          value={topicDetail.priority || 'medium'}
                          onChange={(e) => handlePriorityChange(e.target.value as FeedbackTopic['priority'])}
                          disabled={submittingReply}
                        >
                          <option value="low">低</option>
                          <option value="medium">中</option>
                          <option value="high">高</option>
                          <option value="urgent">紧急</option>
                        </select>
                      </div>
                    </div>

                    <div className="admin-actions-buttons">
                      <button
                        className={`btn ${topicDetail.is_pinned ? 'btn-warning' : 'btn-outline-warning'} admin-btn`}
                        onClick={handleTogglePin}
                        disabled={submittingReply}
                        title={topicDetail.is_pinned ? '取消置顶' : '置顶主题'}
                      >
                        <i className={`fas ${topicDetail.is_pinned ? 'fa-thumbtack' : 'fa-thumbtack'} me-1`}></i>
                        {topicDetail.is_pinned ? '取消置顶' : '置顶'}
                      </button>

                      <button
                        className="btn btn-danger admin-btn"
                        onClick={handleDeleteTopic}
                        disabled={submittingReply}
                        title="删除主题"
                      >
                        <i className="fas fa-trash me-1"></i>
                        删除
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="footer-buttons">
            <button className="btn btn-secondary" onClick={onHide}>
              关闭
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackDetailModal;
